<template>
  <div class="dashboard">
    <b-row>
      <b-col class="d-flex mb-2 mb-md-4">
        <NotificationsWidget
          :notifications="notifications"
          :loading="notifications == null"
        />
      </b-col>
    </b-row>
    <b-row class="d-flex d-sm-none mb-2 mb-md-4">
      <b-col>
        <b-card no-body>
          <b-tabs v-model="dashboardWidgetTab" card fill>
            <b-tab no-body title="Latest decks">
              <DecksWidget :decks="latestDecks" :perPage="perPage" />
            </b-tab>
            <b-tab no-body title="My decks">
              <DecksWidget
                :decks="userDecks"
                hide-username
                date-field="updated_on"
                :perPage="perPage"
              >
                <template #no-results>
                  <em>
                    You don't have any decks. Go
                    <router-link :to="{ name: 'CreateDeck' }">
                      make some</router-link
                    >!
                  </em>
                </template>
              </DecksWidget>
            </b-tab>
          </b-tabs>
          <template #footer>
            <router-link
              v-if="dashboardWidgetTab === 0"
              :to="{ name: 'DeckList' }"
              >More fan decks</router-link
            >
            <router-link
              v-if="dashboardWidgetTab === 1"
              :to="{
                name: 'UserDeckList',
                params: { username: username },
              }"
              >More of my decks
            </router-link>
          </template>
        </b-card>
      </b-col>
    </b-row>
    <b-row class="d-none d-sm-flex mb-2 mb-md-4">
      <b-col>
        <b-card-group deck>
          <b-card no-body>
            <template #header> Latest decks </template>
            <DecksWidget :decks="latestDecks" :perPage="perPage" />
            <template #footer>
              <router-link :to="{ name: 'DeckList' }"
                >More fan decks</router-link
              >
            </template>
          </b-card>

          <b-card no-body>
            <template #header> My decks </template>
            <DecksWidget
              :decks="userDecks"
              hide-username
              date-field="updated_on"
              :perPage="perPage"
            >
              <template #no-results>
                <em>
                  You don't have any decks. Go
                  <router-link :to="{ name: 'CreateDeck' }">
                    make some</router-link
                  >!
                </em>
              </template>
            </DecksWidget>
            <template #footer>
              <router-link
                :to="{
                  name: 'UserDeckList',
                  params: { username: username },
                }"
                >More of my decks
              </router-link>
            </template>
          </b-card>
        </b-card-group>
      </b-col>
    </b-row>
    <NewsWidgetResponsive
      :newsPosts="newsPosts"
      :newsPostCount="newsPostCount"
      cols="1"
      cols-md="2"
      cols-lg="3"
      breakpoint="md"
    />
    <b-toast
      id="retry-toast"
      variant="danger"
      no-hover-pause
      v-model="showRetryToast"
      :auto-hide-delay="retryInterval * 1000"
    >
      Server error. Retrying in {{ retryCountdown }} seconds...
    </b-toast>
  </div>
</template>

<script>
import DecksWidget from "@/components/DecksWidget.vue";
import NotificationsWidget from "@/components/NotificationsWidget.vue";
import NewsWidgetResponsive from "@/components/NewsWidgetResponsive.vue";

import api from "@/api.js";
export default {
  components: {
    NotificationsWidget,
    DecksWidget,
    NewsWidgetResponsive,
  },
  name: "Dashboard",
  metaInfo: {
    title: "Dashboard",
  },
  data() {
    return {
      perPage: 5,
      newsPostCount: 6,
      latestDecks: null,
      userDecks: null,
      newsPosts: null,
      retryCountdownTimer: null,
      retryTimer: null,
      retryCountdown: null,
      retryInterval: 5,
      showRetryToast: false,
    };
  },
  methods: {
    getRndInteger(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    async getLatestDecks() {
      const data = await api.getDecks({
        sortBy: "first_published",
        sortDesc: true,
        perPage: this.perPage,
      });
      this.latestDecks = data.decks;
    },
    async getUserDecks() {
      const data = await api.getUserDecks(this.username, {
        sortBy: "updated",
        sortDesc: true,
        perPage: this.perPage,
      });
      this.userDecks = data.decks;
    },
    async getNewsPosts() {
      const data = await api.getPosts({ perPage: this.newsPostCount });
      this.newsPosts = data.posts;
    },
    async loadData() {
      try {
        await Promise.all([
          this.$store.dispatch("getNotificationSummary"),
          this.getLatestDecks(),
          this.getUserDecks(),
          this.getNewsPosts(),
        ]);
        this.showRetryToast = false;
        clearInterval(this.retryTimer);
      } catch (err) {
        console.error(err);
        if (!this.showRetryToast) {
          this.retryCountdown = this.retryInterval;
          this.showRetryToast = true;
          this.retryCountdownTimer = setInterval(() => {
            this.retryCountdown -= 1;
            if (this.retryCountdown < 1) {
              clearInterval(this.retryCountdownTimer);
            }
          }, 1000);
          this.retryTimer = setTimeout(
            this.loadData,
            this.retryInterval * 1000 + 500
          );
        }
      }
    },
  },
  computed: {
    showWelcomeAlert: {
      get() {
        if ("showDashboardWelcome" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.showDashboardWelcome;
        }
        return true;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "showDashboardWelcome",
          value: value,
        });
      },
    },
    dashboardWidgetTab: {
      get() {
        if ("dashboardWidgetTab" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.dashboardWidgetTab;
        }
        return 0;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "dashboardWidgetTab",
          value: value,
        });
      },
    },
    username() {
      return this.$store.getters.currentUser;
    },
    notifications() {
      return this.$store.state.userNotifications;
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss">
.dashboard {
  .news-post.card {
    height: 24em;
    overflow: scroll;
  }
}
</style>
